/**
 * ShoppingListView - Displays the shopping bag items at end of assessment
 */

import React, { useState } from 'react';
import { colors, fonts, spacing, radii } from '../styles/theme';
import { ShoppingListItem } from '../types';

interface ShoppingListViewProps {
    items: ShoppingListItem[];
    phase: string;
    area: string;
}

const ShoppingListView: React.FC<ShoppingListViewProps> = ({ items, phase, area }) => {
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    const toggleItem = (questionId: string) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(questionId)) {
            newExpanded.delete(questionId);
        } else {
            newExpanded.add(questionId);
        }
        setExpandedItems(newExpanded);
    };

    if (items.length === 0) {
        return (
            <div style={styles.empty}>
                <svg 
                    width="48" 
                    height="48" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={colors.textMuted}
                    strokeWidth="1.5"
                >
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                <p style={styles.emptyTitle}>Your shopping bag is empty</p>
                <p style={styles.emptyText}>
                    All assessed items were answered with "yes" or "no".
                </p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <p style={styles.subtitle}>
                {items.length} item{items.length !== 1 ? 's' : ''} in your bag
            </p>
            
            <div style={styles.list}>
                {items.map((item, index) => (
                    <div key={item.questionId} style={styles.item}>
                        <div style={styles.itemHeader}>
                            <span style={styles.itemNumber}>{index + 1}</span>
                            <span style={styles.itemId}>{item.questionId}</span>
                        </div>
                        <p style={styles.itemQuestion}>{item.question}</p>
                        
                        {item.detailedQuestion && (
                            <div style={styles.detailedQuestionSection}>
                                <span style={styles.detailedQuestionLabel}>The Why:</span>
                                <p style={styles.detailedQuestionText}>{item.detailedQuestion}</p>
                            </div>
                        )}

                        {item.recommendation && (
                            <div style={styles.recommendationSection}>
                                <span style={styles.recommendationLabel}>Best practice:</span>
                                <p style={styles.recommendationText}>{item.recommendation}</p>
                            </div>
                        )}

                        {/* Collapsible related questions */}
                        {item.stakeholderSuggestions.length > 0 && (
                            <div style={styles.childrenSection}>
                                <button
                                    onClick={() => toggleItem(item.questionId)}
                                    style={styles.dropdownButton}
                                    aria-expanded={expandedItems.has(item.questionId)}
                                >
                                    <span style={styles.dropdownIcon}>
                                        {expandedItems.has(item.questionId) ? '▼' : '▶'}
                                    </span>
                                    You may also consider these related questions
                                </button>
                                
                                {expandedItems.has(item.questionId) && (
                                    <div style={styles.childrenList}>
                                        {item.stakeholderSuggestions.map((childQuestion, i) => (
                                            <div key={i} style={styles.childBubble}>
                                                {childQuestion}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {},
    empty: {
        textAlign: 'center',
        padding: spacing[12],
    },
    emptyTitle: {
        fontSize: fonts.lg,
        fontWeight: fonts.medium,
        color: colors.text,
        marginTop: spacing[4],
        marginBottom: spacing[2],
    },
    emptyText: {
        fontSize: fonts.sm,
        color: colors.textMuted,
        margin: 0,
    },
    subtitle: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        marginBottom: spacing[4],
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
    },
    item: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        padding: spacing[4],
    },
    itemHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        marginBottom: spacing[2],
    },
    itemNumber: {
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.primary,
        color: colors.white,
        fontSize: fonts.sm,
        fontWeight: fonts.bold,
        borderRadius: radii.full,
    },
    itemId: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        fontFamily: 'monospace',
    },
    itemQuestion: {
        fontSize: fonts.base,
        color: colors.text,
        lineHeight: fonts.lineHeightNormal,
        margin: `0 0 ${spacing[3]} 0`,
    },
    detailedQuestionSection: {
        backgroundColor: '#fafaf5',
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        padding: spacing[3],
        marginBottom: spacing[3],
    },
    detailedQuestionLabel: {
        display: 'block',
        fontSize: fonts.sm,
        fontWeight: fonts.bold,
        color: colors.text,
        marginBottom: spacing[2],
    },
    detailedQuestionText: {
        fontSize: fonts.sm,
        color: colors.text,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
    },
    recommendationSection: {
        backgroundColor: '#f5faf7',
        border: `1px solid ${colors.primary}`,
        borderRadius: radii.md,
        padding: spacing[3],
        marginBottom: spacing[3],
    },
    recommendationLabel: {
        display: 'block',
        fontSize: fonts.sm,
        fontWeight: fonts.bold,
        color: colors.primary,
        marginBottom: spacing[2],
    },
    recommendationText: {
        fontSize: fonts.sm,
        color: colors.text,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
    },
    stakeholders: {
        borderTop: `1px solid ${colors.borderLight}`,
        paddingTop: spacing[3],
        marginTop: spacing[2],
    },
    stakeholderLabel: {
        fontSize: fonts.xs,
        fontWeight: fonts.medium,
        color: colors.textMuted,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        display: 'block',
        marginBottom: spacing[2],
    },
    stakeholderTags: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: spacing[2],
    },
    stakeholderTag: {
        fontSize: fonts.xs,
        color: colors.textSecondary,
        backgroundColor: colors.bgLight,
        padding: `${spacing[1]} ${spacing[2]}`,
        borderRadius: radii.sm,
    },
    childrenSection: {
        marginTop: spacing[3],
        borderTop: `1px solid ${colors.borderLight}`,
        paddingTop: spacing[3],
    },
    dropdownButton: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        background: 'none',
        border: 'none',
        padding: 0,
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.text,
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left' as const,
    },
    dropdownIcon: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        transition: 'transform 0.2s ease',
    },
    childrenList: {
        marginTop: spacing[3],
        display: 'flex',
        flexDirection: 'column' as const,
        gap: spacing[2],
    },
    childBubble: {
        fontSize: fonts.sm,
        color: colors.text,
        backgroundColor: colors.bgLight,
        padding: `${spacing[2]} ${spacing[3]}`,
        borderRadius: radii.md,
        fontWeight: fonts.medium,
        border: `1px solid ${colors.border}`,
    },
};

export default ShoppingListView;
